import * as React from "react";
import { GripVertical, UsersRound } from "lucide-react";

import {
  addAvailabilityPatternMember,
  listAvailabilityPatterns,
  listRelationships,
  removeAvailabilityPatternMember,
} from "@/api/client";
import { AvailabilityPatternView, RelationshipView } from "@/api/types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** People you've met but never filed land here until you drag them onto a type. */
const UNCATEGORIZED = "Uncategorized";
const NOT_YET_BOOKED = "Not booked yet";

/**
 * "Your platform connections" — everyone you're connected with on the platform,
 * as a borderless canvas of pastel availability-pattern lanes filled with round avatars.
 *
 * <p>The lanes are your <em>own</em> availability patterns (directional: what
 * <em>you</em> filed each person under). Filing is additive — the same face can
 * live in several lanes at once (a colleague who is also a client). You file by
 * <strong>dragging a face onto a lane</strong>; the click-popover is the
 * keyboard-accessible fallback and also carries the card deep-link and an
 * un-file toggle.</p>
 *
 * <p>People who invited you sit in {@link UNCATEGORIZED} until filed; fresh
 * connections with no meeting yet sit in {@link NOT_YET_BOOKED}. Neither is a
 * drop target — you drag <em>out</em> of them, not into them. Refetches whenever
 * {@code refreshKey} changes (the home page bumps it on every accepted booking).</p>
 */
export function PlatformConnectionsPanel({ refreshKey }: { refreshKey: number }) {
  const [rows, setRows] = React.useState<RelationshipView[] | null>(null);
  const [myTypes, setMyTypes] = React.useState<AvailabilityPatternView[]>([]);
  const [localKey, setLocalKey] = React.useState(0);
  // The peer currently being dragged (peer_user_id), so lanes can light up as
  // valid drop targets. Cleared on drop / drag-end.
  const [draggingPeer, setDraggingPeer] = React.useState<string | null>(null);
  // The peer currently hovered/opened anywhere in the panel. Filing is additive,
  // so one person can appear in several lanes; highlighting by id makes every
  // copy of them glow together, so you can see at a glance where they're filed.
  const [highlightPeer, setHighlightPeer] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([listRelationships(), listAvailabilityPatterns()])
      .then(([relationships, types]) => {
        if (cancelled) return;
        setRows(relationships);
        setMyTypes(types);
      })
      .catch(() => {
        /* rail widget — swallow errors; the main page surfaces them */
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, localKey]);

  const groups = React.useMemo(() => groupByType(rows ?? [], myTypes), [rows, myTypes]);
  const refresh = React.useCallback(() => setLocalKey((k) => k + 1), []);

  // Optimism-free but snappy: fire the membership change, then refetch. Skips
  // no-ops (already in that lane) so a stray drop doesn't churn the server.
  const fileInto = React.useCallback(
    async (typeId: string, peerId: string, alreadyIn: boolean) => {
      if (alreadyIn) return;
      try {
        await addAvailabilityPatternMember(typeId, peerId);
        refresh();
      } catch {
        /* the next refresh reconciles */
      }
    },
    [refresh],
  );

  return (
    <section className="comic-shadow relative overflow-hidden rounded-3xl border border-border border-l-4 border-l-brand-teal bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-background/70 shadow-sm">
            <UsersRound className="icon-breathe h-4 w-4 text-brand-teal" />
          </span>
          <h2 className="font-display text-sm font-bold tracking-tight text-foreground">
            Your platform connections
          </h2>
        </div>
        {rows && rows.length > 0 && (
          <span className="font-display text-sm font-semibold tabular-nums text-foreground">
            {rows.length}
          </span>
        )}
      </div>

      {rows === null ? (
        <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-muted/40 px-4 py-6 text-center text-sm leading-relaxed text-muted-foreground">
          No connections yet. Invite someone through your agent and they'll appear
          here, filed under the availability pattern you used.
        </div>
      ) : (
        <>
          <div className="max-h-[22rem] space-y-2.5 overflow-y-auto pr-1">
            {groups.map((g) => (
              <ConnectionLane
                key={g.label}
                group={g}
                myTypes={myTypes}
                draggingPeer={draggingPeer}
                onDragPeer={setDraggingPeer}
                highlightPeer={highlightPeer}
                onHighlight={setHighlightPeer}
                onFile={fileInto}
                onChanged={refresh}
              />
            ))}
          </div>
          {myTypes.length > 0 && (
            <p className="mt-3 flex items-center gap-1.5 text-xs leading-tight text-muted-foreground">
              <GripVertical className="h-3 w-3 shrink-0" />
              Drag a face onto a type to file them — they can sit in several.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One pastel lane — an availability pattern (a drop target) or the Uncategorized /
 * Not-booked buckets (drag-out only). Lights up when a face is dragged over a
 * lane it isn't already in.
 */
function ConnectionLane({
  group,
  myTypes,
  draggingPeer,
  onDragPeer,
  highlightPeer,
  onHighlight,
  onFile,
  onChanged,
}: {
  group: Group;
  myTypes: AvailabilityPatternView[];
  draggingPeer: string | null;
  onDragPeer: (peerId: string | null) => void;
  highlightPeer: string | null;
  onHighlight: (peerId: string | null) => void;
  onFile: (typeId: string, peerId: string, alreadyIn: boolean) => void;
  onChanged: () => void;
}) {
  const [over, setOver] = React.useState(false);
  // Lanes can hold dozens of people; show a legible first row (~12) and tuck the
  // rest behind a "+N" chip that expands the lane in place to explore them all.
  const [showAll, setShowAll] = React.useState(false);
  const laneRef = React.useRef<HTMLDivElement>(null);
  const CAP = 12;
  const overflow = group.contacts.length - CAP;
  const shown = showAll ? group.contacts : group.contacts.slice(0, CAP);
  const theme = laneTheme(group);
  const isDropTarget = group.typeId != null;
  // Whether the dragged peer is already in this lane (so we don't glow a no-op).
  const draggedAlreadyHere =
    draggingPeer != null && group.contacts.some((c) => c.peer_user_id === draggingPeer);
  const canAccept = isDropTarget && draggingPeer != null && !draggedAlreadyHere;

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setOver(false);
    const peerId = e.dataTransfer.getData("text/plain");
    if (!peerId || group.typeId == null) return;
    const alreadyIn = group.contacts.some((c) => c.peer_user_id === peerId);
    onFile(group.typeId, peerId, alreadyIn);
  }

  return (
    <div
      ref={laneRef}
      onDragOver={(e) => {
        if (!canAccept) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={isDropTarget ? onDrop : undefined}
      className={cn(
        "rounded-2xl px-3 py-2.5 transition-all duration-150",
        theme.bg,
        canAccept && "ring-1 ring-inset",
        canAccept && theme.ring,
        over && "scale-[1.01] ring-2 ring-offset-0",
        over && theme.ringStrong,
      )}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={cn("font-display text-xs font-bold uppercase tracking-wider", theme.text)}>
          {group.label}
        </span>
        <span className={cn("rounded-full px-1.5 text-xs font-semibold tabular-nums", theme.badge)}>
          {group.contacts.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((c) => (
          <ContactAvatar
            key={c.peer_user_id}
            contact={c}
            myTypes={myTypes}
            onDragPeer={onDragPeer}
            highlighted={highlightPeer === c.peer_user_id}
            onHighlight={onHighlight}
            onChanged={onChanged}
          />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => {
              setShowAll((v) => !v);
              // Bring the (now taller) lane into view so the extra faces are
              // reachable without hunting for the scrollbar.
              if (!showAll) {
                window.requestAnimationFrame(() =>
                  laneRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
                );
              }
            }}
            title={showAll ? "Show fewer" : `Show ${overflow} more`}
            aria-label={showAll ? "Show fewer connections" : `Show ${overflow} more connections`}
            className={cn(
              "grid h-8 w-8 place-items-center rounded-full border border-dashed text-xs font-bold tabular-nums transition hover:bg-brand-blue/10",
              theme.text,
              "border-current/40",
            )}
          >
            {showAll ? "–" : `+${overflow}`}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A round gradient avatar. Draggable (to file into a lane) and clickable (opens
 * a popover with the person's card and per-type file/un-file toggles — the
 * keyboard-accessible path, since drag is pointer-only).
 */
function ContactAvatar({
  contact,
  myTypes,
  onDragPeer,
  highlighted,
  onHighlight,
  onChanged,
}: {
  contact: RelationshipView;
  myTypes: AvailabilityPatternView[];
  onDragPeer: (peerId: string | null) => void;
  highlighted: boolean;
  onHighlight: (peerId: string | null) => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const name = contact.handle ? `@${contact.handle}` : contact.display_name;
  const initial = (contact.handle || contact.display_name || "?").charAt(0).toUpperCase();
  const tip =
    contact.meeting_count > 1
      ? `${name} · ${contact.meeting_count} meetings — drag to file`
      : contact.meeting_count === 1
        ? `${name} · 1 meeting — drag to file`
        : `${name} · connected — drag to file`;
  const filedUnder = new Set(contact.types.map((t) => t.id));

  async function toggle(typeId: string) {
    setBusy(true);
    try {
      if (filedUnder.has(typeId)) {
        await removeAvailabilityPatternMember(typeId, contact.peer_user_id);
      } else {
        await addAvailabilityPatternMember(typeId, contact.peer_user_id);
      }
      onChanged();
    } catch {
      /* leave the panel as-is; the next refresh reconciles */
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        // Keep the cross-lane glow lit while the popover is open, clear on close.
        onHighlight(o ? contact.peer_user_id : null);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", contact.peer_user_id);
            e.dataTransfer.effectAllowed = "copy";
            onDragPeer(contact.peer_user_id);
          }}
          onDragEnd={() => onDragPeer(null)}
          onMouseEnter={() => onHighlight(contact.peer_user_id)}
          onMouseLeave={() => {
            // Don't drop the glow out from under an open popover.
            if (!open) onHighlight(null);
          }}
          onFocus={() => onHighlight(contact.peer_user_id)}
          onBlur={() => {
            if (!open) onHighlight(null);
          }}
          title={tip}
          aria-label={tip}
          className={cn(
            "group relative h-8 w-8 cursor-grab rounded-full shadow-sm ring-2 transition active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none",
            avatarToneFor(contact.handle ?? contact.display_name),
            highlighted
              ? "z-10 scale-110 shadow-md ring-brand-blue ring-offset-2 ring-offset-background"
              : "ring-border focus-visible:ring-brand-blue",
          )}
        >
          <span className="grid h-full w-full place-items-center font-display text-xs font-bold text-foreground">
            {initial}
          </span>
          {contact.meeting_count > 1 && (
            <span className="absolute -bottom-0.5 -right-0.5 grid h-3.5 min-w-3.5 place-items-center rounded-full border border-background bg-foreground px-0.5 text-[8px] font-bold tabular-nums text-background">
              {contact.meeting_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 rounded-xl border border-border p-0">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <span
            className={cn(
              "grid h-8 w-8 shrink-0 place-items-center rounded-full font-display text-xs font-bold text-foreground",
              avatarToneFor(contact.handle ?? contact.display_name),
            )}
          >
            {initial}
          </span>
          <div className="min-w-0">
            <p className="truncate font-display text-xs font-semibold">{name}</p>
            <p className="text-xs text-muted-foreground">
              {contact.meeting_count === 0
                ? "Connected — not booked yet"
                : contact.meeting_count === 1
                  ? "1 meeting"
                  : `${contact.meeting_count} meetings`}
            </p>
          </div>
        </div>

        {contact.has_card ? (
          <a
            href={`/@${contact.handle}`}
            className="block px-3 py-2 text-xs font-medium text-brand-blue transition hover:bg-brand-blue/10"
          >
            ↗ View {name}'s card
          </a>
        ) : (
          <p className="px-3 py-2 text-sm text-muted-foreground">Card is private</p>
        )}

        {myTypes.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              File under my types
            </p>
            <div className="space-y-1">
              {myTypes.map((type) => (
                <label
                  key={type.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 text-xs transition hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={filedUnder.has(type.id)}
                    disabled={busy}
                    onChange={() => void toggle(type.id)}
                    className="h-3.5 w-3.5 accent-brand-teal"
                  />
                  {type.name}
                </label>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

type Group = { label: string; typeId: string | null; contacts: RelationshipView[] };

/**
 * Fan each person across every one of the caller's types they are filed under,
 * so a colleague who is also a client appears in both lanes. People with no
 * types form the Uncategorized lane (met but unfiled) or the Not-booked lane
 * (connected, no meeting yet); both always sort last.
 */
function groupByType(rows: RelationshipView[], myTypes: AvailabilityPatternView[]): Group[] {
  const byType = new Map<string, { typeId: string; contacts: RelationshipView[] }>();
  const uncategorized: RelationshipView[] = [];
  const notYetBooked: RelationshipView[] = [];

  for (const row of rows) {
    if (row.types.length === 0) {
      (row.meeting_count === 0 ? notYetBooked : uncategorized).push(row);
      continue;
    }
    for (const type of row.types) {
      const bucket = byType.get(type.name);
      if (bucket) bucket.contacts.push(row);
      else byType.set(type.name, { typeId: type.id, contacts: [row] });
    }
  }

  const groups: Group[] = [];
  for (const [label, { typeId, contacts }] of byType) {
    contacts.sort(byFrequencyThenRecency);
    groups.push({ label, typeId, contacts });
  }

  // Busiest lane first, ties by size, then the caller's own type order so the
  // panel doesn't reshuffle on every booking.
  const typeOrder = new Map(myTypes.map((t, i) => [t.name, i]));
  groups.sort(
    (a, b) =>
      (b.contacts[0]?.meeting_count ?? 0) - (a.contacts[0]?.meeting_count ?? 0) ||
      b.contacts.length - a.contacts.length ||
      (typeOrder.get(a.label) ?? 0) - (typeOrder.get(b.label) ?? 0),
  );

  if (uncategorized.length > 0) {
    uncategorized.sort(byFrequencyThenRecency);
    groups.push({ label: UNCATEGORIZED, typeId: null, contacts: uncategorized });
  }
  if (notYetBooked.length > 0) {
    notYetBooked.sort(byName);
    groups.push({ label: NOT_YET_BOOKED, typeId: null, contacts: notYetBooked });
  }
  return groups;
}

function byFrequencyThenRecency(a: RelationshipView, b: RelationshipView): number {
  return (
    b.meeting_count - a.meeting_count ||
    (b.last_booked_at ?? "").localeCompare(a.last_booked_at ?? "")
  );
}

function byName(a: RelationshipView, b: RelationshipView): number {
  return (a.handle ?? a.display_name).localeCompare(b.handle ?? b.display_name);
}

/** Pastel lane theme — a stable soft tint per availability pattern; neutral for the buckets. */
function laneTheme(group: Group): {
  bg: string;
  text: string;
  badge: string;
  ring: string;
  ringStrong: string;
} {
  if (group.typeId == null) {
    return {
      bg: "bg-background/50",
      text: "text-muted-foreground",
      badge: "bg-muted text-muted-foreground",
      ring: "ring-border",
      ringStrong: "ring-muted-foreground/40",
    };
  }
  // Rotate blue → teal → coral → yellow across the groups. The colour identity
  // lives in the tint and the ring; labels stay on the one ink so they read
  // sharp in both themes. Brand tokens carry their own dark values, so there
  // are no `dark:` twins to maintain.
  const themes = [
    {
      bg: "bg-brand-blue/10",
      text: "text-foreground",
      badge: "bg-brand-blue/20 text-foreground",
      ring: "ring-brand-blue/40",
      ringStrong: "ring-brand-blue",
    },
    {
      bg: "bg-brand-teal/10",
      text: "text-foreground",
      badge: "bg-brand-teal/20 text-foreground",
      ring: "ring-brand-teal/40",
      ringStrong: "ring-brand-teal",
    },
    {
      bg: "bg-brand-coral/10",
      text: "text-foreground",
      badge: "bg-brand-coral/20 text-foreground",
      ring: "ring-brand-coral/40",
      ringStrong: "ring-brand-coral",
    },
    {
      bg: "bg-brand-yellow/15",
      text: "text-foreground",
      badge: "bg-brand-yellow/30 text-foreground",
      ring: "ring-brand-yellow/50",
      ringStrong: "ring-brand-yellow",
    },
  ];
  let hash = 0;
  for (let i = 0; i < group.label.length; i++) hash = (hash * 31 + group.label.charCodeAt(i)) | 0;
  return themes[Math.abs(hash) % themes.length];
}

/**
 * Deterministic avatar tint from the contact key, so a face looks stable.
 * Solid palette tints rather than gradients, cycling blue → teal → coral →
 * yellow; the initial rides on the one ink so it stays legible in both themes.
 */
function avatarToneFor(key: string): string {
  const palette = [
    "bg-brand-blue/20",
    "bg-brand-teal/20",
    "bg-brand-coral/20",
    "bg-brand-yellow/30",
  ];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}
